import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi } from '../services/dashboardApi';
import NotificationBell from './NotificationBell';
import {
  Home, Car, Share2, Store, Settings, Plus, ChevronLeft, ChevronRight,
  ChevronDown, Search, FileText, HeartHandshake, CalendarDays, LogOut, User,
  Sparkles, Clock, Globe, Activity, TrendingDown, Flame, BarChart3
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Data-driven navigation definitions
// ---------------------------------------------------------------------------
const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', href: '/dashboard', icon: Home, desc: 'Vue d\'ensemble et KPIs' },
  { id: 'stock', label: 'Mon stock', href: '/stock', icon: Car, badgeKey: 'stock', desc: 'Gérez votre inventaire' },
  { id: 'social', label: 'Social', href: '/social', icon: Share2, badgeKey: 'social', desc: 'Publications et planification' },
  { id: 'website', label: 'Mon site web', href: '/website', icon: Globe, desc: 'Votre vitrine digitale' },
  { id: 'activity', label: 'Activité', href: '/activity', icon: Activity, desc: 'Historique et événements' },
  { id: 'opportunities', label: 'Opportunités', href: '/opportunities', icon: TrendingDown, desc: 'Véhicules sous-cotés' },
  { id: 'deals', label: 'Deals', href: '/deals', icon: Flame, desc: 'Bonnes affaires détectées' },
  { id: 'market', label: 'Marché', href: '/market', icon: BarChart3, desc: 'Intelligence marché' },
];

const MARKETPLACE_SUBNAV = [
  { label: 'Parcourir', href: '/marketplace', icon: Search, exact: true },
  { label: 'Mes annonces', href: '/marketplace/my-listings', icon: FileText },
  { label: 'Offres', href: '/marketplace/offers', icon: HeartHandshake, badgeKey: 'marketplace' },
  { label: 'Rendez-vous', href: '/marketplace/appointments', icon: CalendarDays },
];

// ---------------------------------------------------------------------------
// localStorage helpers (Safari private-mode safe)
// ---------------------------------------------------------------------------
const safeGetItem = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSetItem = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* noop */ }
};

// ---------------------------------------------------------------------------
// Badge pill component
// ---------------------------------------------------------------------------
const BadgePill = ({ count }) => {
  if (!count || count <= 0) return null;
  return (
    <span className="ml-auto flex-shrink-0 min-w-[20px] h-5 flex items-center justify-center rounded-full bg-carmin text-white text-xs font-semibold px-1.5">
      {count > 99 ? '99+' : count}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Collapsed tooltip wrapper
// ---------------------------------------------------------------------------
const CollapsedTooltip = ({ label, collapsed, children }) => {
  if (!collapsed) return children;
  return (
    <div className="group relative">
      {children}
      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2.5 py-1 rounded-md bg-dark-surface text-primary text-xs font-medium whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50 shadow-lg border border-dark-border">
        {label}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sidebar component
// ---------------------------------------------------------------------------
const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // ---- State ----
  const [collapsed, setCollapsed] = useState(() => {
    const saved = safeGetItem('carmin-sidebar-collapsed');
    return saved === 'true';
  });
  const [marketplaceOpen, setMarketplaceOpen] = useState(false);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [userPopoverOpen, setUserPopoverOpen] = useState(false);
  const [badges, setBadges] = useState({ stock: 0, social: 0, marketplace: 0 });

  // ---- Refs for click-outside detection ----
  const quickActionsRef = useRef(null);
  const userPopoverRef = useRef(null);

  // ---- Derived ----
  const isMarketplaceRoute = location.pathname.startsWith('/marketplace');
  // Marketplace status: 'approved', 'pending', or 'none'
  const marketplaceStatus = (user?.marketplace_approved === true || user?.marketplace_status === 'approved')
    ? 'approved'
    : user?.marketplace_status === 'pending' ? 'pending' : 'none';
  const marketplaceBadgeTotal = badges.marketplace || 0;
  const userInitial = (user?.name || user?.email || '?')[0].toUpperCase();

  // ---- Auto-open marketplace when on marketplace route ----
  useEffect(() => {
    if (isMarketplaceRoute) {
      setMarketplaceOpen(true);
    }
  }, [isMarketplaceRoute]);

  // ---- Persist collapsed state ----
  useEffect(() => {
    safeSetItem('carmin-sidebar-collapsed', String(collapsed));
  }, [collapsed]);

  // ---- Set CSS variable for content offset ----
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-width', collapsed ? '70px' : '260px'
    );
  }, [collapsed]);

  // ---- Fetch badge counts on mount + interval (skip when tab hidden) ----
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const data = await dashboardApi.getBadgeCounts();
        setBadges(data);
      } catch {
        // silently fail — keep previous counts
      }
    };
    fetchBadges();
    const interval = setInterval(() => {
      if (!document.hidden) fetchBadges();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // ---- Click-outside handlers ----
  useEffect(() => {
    const handler = (e) => {
      if (quickActionsRef.current && !quickActionsRef.current.contains(e.target)) {
        setQuickActionsOpen(false);
      }
      if (userPopoverRef.current && !userPopoverRef.current.contains(e.target)) {
        setUserPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ---- Helpers ----
  const isActive = (href, exact = false) => {
    // Handle hrefs with query params (e.g. /settings?tab=vitrine)
    if (href.includes('?')) {
      const [hrefPath, hrefSearch] = href.split('?');
      return location.pathname === hrefPath && location.search === `?${hrefSearch}`;
    }
    if (exact) return location.pathname === href;
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const handleLogout = () => {
    setUserPopoverOpen(false);
    logout();
    navigate('/login');
  };

  const handleQuickAction = (href) => {
    setQuickActionsOpen(false);
    navigate(href);
  };

  // ---- Render ----
  return (
    <aside
      className="fixed left-0 top-0 h-screen z-30 flex flex-col bg-dark-card border-r border-dark-border transition-all duration-200 ease-out"
      style={{ width: collapsed ? 70 : 260 }}
      aria-label="Navigation principale"
    >
      {/* ============================================================ */}
      {/* HEADER: Logo + Notification bell + Collapse toggle           */}
      {/* ============================================================ */}
      <div className={`flex items-center px-4 h-16 flex-shrink-0 border-b border-dark-border ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {/* Logo */}
        <Link to="/dashboard" className="flex items-center min-w-0">
          <div className="w-8 h-8 rounded-lg bg-carmin flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">C</span>
          </div>
          {!collapsed && (
            <span className="ml-2.5 text-lg font-bold text-primary tracking-tight truncate">
              CARMIN
            </span>
          )}
        </Link>

        {/* Right cluster: notification bell + collapse toggle */}
        {!collapsed && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <NotificationBell dropdownAlign="left" />
            <button
              onClick={() => setCollapsed(true)}
              className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-dark-hover transition-colors"
              title="Réduire le menu"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Collapsed: notification bell + expand button in a compact row */}
      {collapsed && (
        <div className="flex flex-col items-center gap-1 py-2 border-b border-dark-border flex-shrink-0">
          <NotificationBell dropdownAlign="left" />
          <button
            onClick={() => setCollapsed(false)}
            className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-dark-hover transition-colors"
            title="Étendre le menu"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ============================================================ */}
      {/* QUICK ACTION: "+ Ajouter un véhicule"                       */}
      {/* ============================================================ */}
      <div className="px-3 mt-4 mb-2 flex-shrink-0">
        {collapsed ? (
          <CollapsedTooltip label="Ajouter" collapsed={collapsed}>
            <button
              onClick={() => navigate('/vehicles/add')}
              className="w-full flex items-center justify-center h-10 rounded-lg bg-carmin text-white hover:bg-carmin-light transition-colors"
            >
              <Plus className="h-5 w-5" />
            </button>
          </CollapsedTooltip>
        ) : (
          <button
            onClick={() => navigate('/vehicles/add')}
            className="w-full flex items-center justify-center gap-2 h-10 rounded-lg bg-carmin text-white hover:bg-carmin-light transition-colors font-medium text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>Ajouter un véhicule</span>
          </button>
        )}
      </div>

      {/* Divider */}
      <div className="mx-3 border-t border-dark-border mb-1" />

      {/* ============================================================ */}
      {/* MAIN NAVIGATION                                              */}
      {/* ============================================================ */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-1 scrollbar-hide" aria-label="Menu principal">
        {/* Primary nav items */}
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.href);
          const badge = item.badgeKey ? badges[item.badgeKey] : 0;

          return (
            <CollapsedTooltip key={item.id} label={item.label} collapsed={collapsed}>
              <Link
                to={item.href}
                className={`relative flex items-center gap-3 rounded-lg px-3 py-2.5 mb-0.5 transition-colors group ${
                  active
                    ? 'bg-dark-hover text-primary'
                    : 'text-secondary hover:text-primary hover:bg-dark-hover'
                }`}
              >
                {/* Active indicator bar */}
                {active && (
                  <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-carmin" />
                )}
                <item.icon className={`h-[18px] w-[18px] flex-shrink-0 mt-0.5 ${active ? 'text-carmin' : ''}`} />
                {!collapsed && (
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{item.label}</span>
                      <BadgePill count={badge} />
                    </div>
                    {item.desc && (
                      <p className="text-[11px] text-muted truncate mt-0.5">{item.desc}</p>
                    )}
                  </div>
                )}
                {/* Badge dot in collapsed mode */}
                {collapsed && badge > 0 && (
                  <div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-carmin" />
                )}
              </Link>
            </CollapsedTooltip>
          );
        })}

      </nav>

      {/* ============================================================ */}
      {/* FOOTER: Settings + User                                       */}
      {/* ============================================================ */}
      {/* ============================================================ */}
      {/* MARKETPLACE SECTION (above footer)                            */}
      {/* ============================================================ */}
      <div className="flex-shrink-0 px-3 pb-1">
        {marketplaceStatus === 'approved' ? (
          /* -- Marketplace with sub-nav (approved users) -- */
          <div>
            <CollapsedTooltip label="Marketplace" collapsed={collapsed}>
              <button
                onClick={() => {
                  if (collapsed) {
                    navigate('/marketplace');
                  } else {
                    setMarketplaceOpen(!marketplaceOpen);
                  }
                }}
                className={`relative w-full flex items-center gap-3 rounded-lg px-3 h-10 mb-0.5 transition-colors ${
                  isMarketplaceRoute
                    ? 'bg-dark-hover text-primary'
                    : 'text-secondary hover:text-primary hover:bg-dark-hover'
                }`}
                aria-expanded={marketplaceOpen}
              >
                {isMarketplaceRoute && (
                  <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-accent-purple" />
                )}
                <Store className={`h-[18px] w-[18px] flex-shrink-0 ${isMarketplaceRoute ? 'text-accent-purple' : ''}`} />
                {!collapsed && (
                  <>
                    <span className="text-sm font-medium truncate">Marketplace</span>
                    <BadgePill count={marketplaceBadgeTotal} />
                    <ChevronDown
                      className={`h-3.5 w-3.5 ml-auto flex-shrink-0 transition-transform duration-200 ${
                        marketplaceOpen ? 'rotate-0' : '-rotate-90'
                      }`}
                    />
                  </>
                )}
                {collapsed && marketplaceBadgeTotal > 0 && (
                  <div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-accent-purple" />
                )}
              </button>
            </CollapsedTooltip>

            {/* Sub-nav (only when expanded and open) */}
            {!collapsed && (
              <div
                className="ml-4 pl-3 border-l border-dark-border overflow-hidden transition-all duration-200 ease-out"
                style={{
                  maxHeight: marketplaceOpen ? `${MARKETPLACE_SUBNAV.length * 40}px` : '0',
                  opacity: marketplaceOpen ? 1 : 0,
                }}
              >
                {MARKETPLACE_SUBNAV.map((sub) => {
                  const subActive = isActive(sub.href, sub.exact);
                  const subBadge = sub.badgeKey ? badges[sub.badgeKey] : 0;
                  return (
                    <Link
                      key={sub.href}
                      to={sub.href}
                      className={`flex items-center gap-2.5 rounded-md px-2.5 h-9 mb-0.5 transition-colors text-sm ${
                        subActive
                          ? 'text-primary bg-dark-hover font-medium'
                          : 'text-muted hover:text-secondary hover:bg-dark-hover'
                      }`}
                    >
                      <sub.icon className={`h-4 w-4 flex-shrink-0 ${subActive ? 'text-accent-purple' : ''}`} />
                      <span className="truncate">{sub.label}</span>
                      <BadgePill count={subBadge} />
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        ) : marketplaceStatus === 'pending' ? (
          /* -- Marketplace pending validation -- */
          collapsed ? (
            <CollapsedTooltip label="Marketplace — En attente" collapsed={collapsed}>
              <div className="relative w-full flex items-center justify-center h-10 rounded-lg text-accent-purple/60 mb-0.5">
                <Store className="h-[18px] w-[18px]" />
                <Clock className="absolute top-1 right-1 w-3 h-3 text-amber-400" />
              </div>
            </CollapsedTooltip>
          ) : (
            <div className="rounded-lg border border-accent-purple/20 bg-accent-purple-bg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-medium text-primary">Marketplace</span>
              </div>
              <p className="text-xs text-muted">En cours de validation</p>
            </div>
          )
        ) : (
          /* -- Marketplace gated card (non-approved, not requested) -- */
          collapsed ? (
            <CollapsedTooltip label="Marketplace Pro" collapsed={collapsed}>
              <button
                onClick={() => navigate('/marketplace')}
                className="relative w-full flex items-center justify-center h-10 rounded-lg text-accent-purple hover:bg-dark-hover transition-colors mb-0.5"
              >
                <Store className="h-[18px] w-[18px]" />
                <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-accent-purple animate-pulse" />
              </button>
            </CollapsedTooltip>
          ) : (
            <button
              onClick={() => navigate('/marketplace')}
              className="w-full mb-1 rounded-lg border border-accent-purple/40 bg-accent-purple-bg p-3 text-left transition-all hover:border-accent-purple/70 animate-glow-pulse group"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="h-4 w-4 text-accent-purple" />
                <span className="text-sm font-semibold text-accent-purple-light">Marketplace Pro</span>
              </div>
              <p className="text-xs text-muted leading-relaxed mb-2">
                Vendez à des pros vérifiés
              </p>
              <span className="text-xs font-medium text-accent-purple group-hover:text-accent-purple-light transition-colors">
                Découvrir &rarr;
              </span>
            </button>
          )
        )}
      </div>

      <div className="flex-shrink-0 border-t border-dark-border px-3 pt-2 pb-3">
        {/* Settings link */}
        <CollapsedTooltip label="Paramètres" collapsed={collapsed}>
          <Link
            to="/settings"
            className={`relative flex items-center gap-3 rounded-lg px-3 h-10 mb-1 transition-colors ${
              isActive('/settings')
                ? 'bg-dark-hover text-primary'
                : 'text-secondary hover:text-primary hover:bg-dark-hover'
            }`}
          >
            {isActive('/settings') && (
              <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-carmin" />
            )}
            <Settings className={`h-[18px] w-[18px] flex-shrink-0 ${isActive('/settings') ? 'text-carmin' : ''}`} />
            {!collapsed && (
              <span className="text-sm font-medium">Paramètres</span>
            )}
          </Link>
        </CollapsedTooltip>

        {/* User section */}
        <div className="relative" ref={userPopoverRef}>
          <CollapsedTooltip label={user?.name || 'Mon profil'} collapsed={collapsed}>
            <button
              onClick={() => setUserPopoverOpen(!userPopoverOpen)}
              className="w-full flex items-center gap-3 rounded-lg px-3 h-11 transition-colors text-secondary hover:text-primary hover:bg-dark-hover"
              aria-expanded={userPopoverOpen}
              aria-haspopup="true"
            >
              {/* Avatar circle */}
              <div className="w-8 h-8 rounded-full bg-carmin/20 border border-carmin/40 flex items-center justify-center flex-shrink-0">
                <span className="text-carmin text-sm font-semibold">{userInitial}</span>
              </div>
              {!collapsed && (
                <div className="min-w-0 text-left">
                  <div className="text-sm font-medium text-primary truncate">{user?.name || 'Utilisateur'}</div>
                  {user?.company && (
                    <div className="text-xs text-muted truncate">{user.company}</div>
                  )}
                </div>
              )}
            </button>
          </CollapsedTooltip>

          {/* User popover */}
          {userPopoverOpen && (
            <div
              className={`absolute ${collapsed ? 'left-[70px] bottom-0' : 'left-0 right-0 bottom-full mb-1'} bg-dark-surface rounded-lg border border-dark-border shadow-xl z-50 py-1 overflow-hidden`}
            >
              {/* User info header (in popover) */}
              {!collapsed && (
                <div className="px-3 py-2.5 border-b border-dark-border">
                  <div className="text-sm font-medium text-primary truncate">{user?.name || 'Utilisateur'}</div>
                  <div className="text-xs text-muted truncate">{user?.email}</div>
                </div>
              )}
              <button
                onClick={() => {
                  setUserPopoverOpen(false);
                  navigate('/settings');
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-secondary hover:text-primary hover:bg-dark-hover transition-colors text-left"
              >
                <User className="h-4 w-4 flex-shrink-0" />
                Mon profil
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-dark-hover transition-colors text-left"
              >
                <LogOut className="h-4 w-4 flex-shrink-0" />
                Déconnexion
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
